/**
 * Parses the raw `window.OnetrustActiveGroups` string (e.g. ",C0001,C0002,")
 * into a clean comma-separated string of active group IDs (e.g. "C0001,C0002").
 */
export const parseActiveGroups = (raw: string | undefined): string =>
	(raw ?? '').split(',').filter(Boolean).join(',');

/**
 * Registers `callback` to be invoked with the current active-groups string
 * whenever OneTrust initialises or the user changes their consent preferences.
 *
 * - If `window.OneTrust` already exists the SDK is loaded: we call `callback`
 *   immediately with the current value and register an `OnConsentChanged` listener.
 * - Otherwise we define `window.OptanonWrapper`, which OneTrust calls once it
 *   has written the initial value to `window.OnetrustActiveGroups`.
 *
 * NOTE: Only one `window.OptanonWrapper` can be active at a time. If another
 * library also sets it, chain the calls manually.
 */
export const onActiveGroupsChange = (callback: (activeGroups: string) => void): void => {
	const handleChange = (): void => callback(parseActiveGroups(window.OnetrustActiveGroups));

	if (window.OneTrust && window.OnetrustActiveGroups) {
		// SDK fully initialised: groups already available – fire immediately and subscribe.
		handleChange();
		window.OneTrust.OnConsentChanged(handleChange);
	} else {
		// SDK not yet ready or groups not yet written – wait for OptanonWrapper.
		window.OptanonWrapper = (): void => {
			handleChange();
			window.OneTrust?.OnConsentChanged(handleChange);
		};
	}
};
